import { jsx, jsxs } from "react/jsx-runtime";
import { URL_PRODUCTS_AD_BUDGETS } from "@seek/adv-constants";
import { useTranslations } from "@vocab/react";
import { Box, Columns, Column, Spread, Stack, Text, Inline, ButtonLink } from "braid-design-system";
import * as React from "react";
import { createContext, useState, useContext, useCallback } from "react";
import { createUrlResolver } from "@seek/melways-sites";
import { translations } from "../../../../HirerContractBalance/components/AdBudgetsUpsell/.vocab/index.mjs";
import { icon } from "../../HirerBalancePresentation.css.mjs";
const HirerBalanceLayout = ({ children }) => /* @__PURE__ */ jsx(
  Box,
  {
    padding: "large",
    borderRadius: "large",
    background: "formAccentSoft",
    height: "full",
    children
  }
);
const HirerBalanceContext = createContext(
  {}
);
const HirerBalanceContextProvider = ({
  children,
  site,
  language,
  isStaging,
  advertiserDetails,
  locale
}) => {
  const [calloutVisible, setCalloutVisible] = useState(false);
  const [contractBalance, setContractBalance] = useState();
  const [warningType, setWarningType] = useState("NoWarning");
  const showCallout = (contractBalanceToShow, warningTypeForContract) => {
    setCalloutVisible(true);
    setContractBalance(contractBalanceToShow);
    setWarningType(warningTypeForContract);
  };
  const hideCallout = () => {
    setCalloutVisible(false);
  };
  const urlResolver = createUrlResolver({
    site,
    language,
    staging: isStaging
  });
  const usersContext = {
    calloutVisible,
    contractBalance,
    warningType,
    advertiserDetails,
    showCallout,
    hideCallout,
    urlResolver,
    locale
  };
  return /* @__PURE__ */ jsx(HirerBalanceContext.Provider, { value: usersContext, children });
};
const { Consumer } = HirerBalanceContext;
const advertiserHasStackedContracts = (contractBalances) => {
  const advertiserTotalContracts = contractBalances.reduce(
    (aggregator, balanceItem) => balanceItem.contracts ? aggregator + balanceItem.contracts.length : aggregator,
    0
  );
  return advertiserTotalContracts > 1;
};
const createImpressionTrackingData = ({
  contractBalances,
  pricingModel
}) => ({
  advertiserTypePricing: pricingModel === "FIXED" ? "fixed" : "variable",
  advertiserHasStackedContracts: advertiserHasStackedContracts(contractBalances)
});
const defaultTrackingCallback = () => {
};
const TrackingContext = React.createContext(
  defaultTrackingCallback
);
const TrackingProvider = ({ children, context }) => /* @__PURE__ */ jsx(TrackingContext.Provider, { value: context, children });
const onImpression = (trackingCallback) => (advertiserDetails) => {
  const impressionTrackingData = createImpressionTrackingData(advertiserDetails);
  return trackingCallback({
    eventName: ["balance_widget_displayed"],
    data: {
      linkName: "balance_widget_displayed",
      ...impressionTrackingData
    }
  });
};
const onBalanceWidgetClicked = (trackingCallback) => (input) => {
  const impressionTrackingData = createImpressionTrackingData(input);
  return trackingCallback({
    eventName: ["balance_widget_pressed"],
    data: {
      ...impressionTrackingData,
      linkName: "balance_widget_pressed",
      actionOrigin: input.actionOrigin,
      objectInteraction: input.objectInteraction
    }
  });
};
const useTracking = () => {
  const trackingContext = useContext(TrackingContext);
  const trackingCallback = trackingContext ?? defaultTrackingCallback;
  return {
    onImpression: useCallback(
      (advertiserDetails) => onImpression(trackingCallback)(advertiserDetails),
      [trackingCallback]
    ),
    onBalanceWidgetClicked: onBalanceWidgetClicked(trackingCallback)
  };
};
const PriceTags = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "96",
    height: "96",
    viewBox: "0 0 128 128",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M89.8094 43.0413L77.5198 25.4945C74.3184 20.9235 68.2977 19.3102 63.2394 21.6681L43.8229 30.7193C40.7413 32.1557 38.4529 34.8827 37.5729 38.1669L24.5159 86.8964C22.8718 93.0321 26.513 99.3388 32.6487 100.983L64.3548 109.478C70.4905 111.123 76.7972 107.481 78.4412 101.346L91.4983 52.6162C92.3783 49.332 91.7602 45.8262 89.8094 43.0413Z",
          fill: "#6E56E6"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M97.6647 37.9195L80.8342 23.8006C76.4498 20.1224 70.0579 20.1224 65.6732 23.8006L48.8427 37.9195C46.1714 40.1603 44.6284 43.4689 44.6284 46.9556V98.6899C44.6284 105.204 49.9091 110.485 56.4231 110.485H90.0843C96.5984 110.485 101.879 105.204 101.879 98.6899V46.9556C101.879 43.469 100.336 40.1603 97.6647 37.9195ZM73.2537 43.6523C69.9343 43.6523 67.2433 40.9609 67.2433 37.6417C67.2433 34.322 69.9343 31.6311 73.2537 31.6311C76.5734 31.6311 79.2643 34.322 79.2643 37.6417C79.2643 40.9609 76.5734 43.6523 73.2537 43.6523Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M71.2922 69.0543H75.2155C76.2489 69.1655 77.0659 69.9826 77.1772 71.0159C77.0519 72.0432 76.2427 72.8524 75.2155 72.9776H69.3305C68.2472 72.9776 67.3688 73.856 67.3688 74.9393C67.3688 76.0226 68.2472 76.901 69.3305 76.901H71.2922V78.8626C71.2922 79.9459 72.1705 80.8243 73.2538 80.8243C74.3371 80.8243 75.2155 79.9459 75.2155 78.8626V76.901C77.3182 76.901 79.2605 75.7791 80.3123 73.9585C81.3632 72.1378 81.3632 69.8941 80.3123 68.0735C79.2606 66.2528 77.3182 65.131 75.2155 65.131H71.2922C70.2089 65.131 69.3305 64.2526 69.3305 63.1693C69.3305 62.086 70.2089 61.2076 71.2922 61.2076H77.1772C78.2605 61.2076 79.1388 60.3292 79.1388 59.2459C79.1388 58.1627 78.2605 57.2843 77.1772 57.2843H75.2155V55.3226C75.2155 54.2393 74.3371 53.3609 73.2538 53.3609C72.1705 53.3609 71.2922 54.2393 71.2922 55.3226V57.2843C69.1895 57.2843 67.2472 58.4061 66.1954 60.2268C65.1445 62.0474 65.1445 64.2911 66.1954 66.1118C67.2471 67.9325 69.1895 69.0543 71.2922 69.0543Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M73.2539 35.5518C71.7578 35.5518 70.5449 34.3389 70.5449 32.8428V14.0977C70.5449 12.6016 71.7578 11.3887 73.2539 11.3887C74.75 11.3887 75.9629 12.6016 75.9629 14.0977V32.8428C75.9629 34.3389 74.75 35.5518 73.2539 35.5518Z",
          fill: "#FDD651"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M91.6577 88.7129H56.6558V91.6787H91.6577V88.7129Z",
          fill: "#F470BD"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M88.4614 96.9224H59.8521V99.8882H88.4614V96.9224Z",
          fill: "#F470BD"
        }
      )
    ]
  }
);
const AdBudgetsUpsell = () => {
  const { t } = useTranslations(translations);
  const { onBalanceWidgetClicked: onBalanceWidgetClicked2 } = useTracking();
  const { advertiserDetails, urlResolver } = useContext(HirerBalanceContext);
  const onViewDiscountsButtonClicked = () => {
    onBalanceWidgetClicked2({
      ...advertiserDetails,
      actionOrigin: "casual",
      objectInteraction: "view discounts"
    });
  };
  return /* @__PURE__ */ jsx(HirerBalanceLayout, { children: /* @__PURE__ */ jsxs(Columns, { space: "medium", collapseBelow: "desktop", reverse: true, children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
      Box,
      {
        className: icon,
        display: "flex",
        padding: "large",
        borderRadius: "full",
        background: "surface",
        justifyContent: "center",
        alignItems: "center",
        children: /* @__PURE__ */ jsx(PriceTags, {})
      }
    ) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Spread, { direction: "vertical", space: "medium", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(Text, { component: "header", size: "large", weight: "strong", children: t("Pay upfront and save!") }),
        /* @__PURE__ */ jsx(Text, { component: "p", children: t("Pay upfront and secure a discount on your ads.") })
      ] }),
      /* @__PURE__ */ jsx(Inline, { space: "medium", alignY: "center", collapseBelow: "tablet", children: /* @__PURE__ */ jsx(
        ButtonLink,
        {
          href: urlResolver({ path: URL_PRODUCTS_AD_BUDGETS }),
          onClick: onViewDiscountsButtonClicked,
          children: t("View discounts")
        }
      ) })
    ] }) })
  ] }) });
};
export {
  AdBudgetsUpsell,
  HirerBalanceContext,
  HirerBalanceContextProvider,
  HirerBalanceLayout,
  PriceTags,
  TrackingProvider,
  useTracking
};
